import { useT } from '@hana/i18n';
import { LAND_PATH, PORT_LL } from './worldMap';
import { isVnPort } from './svcmap';
import './ServiceMap.i18n';
import m from './lanedetail.module.css';

const MAP_W = 760;
const MAP_H = 400;
const BX = (lon: number) => ((lon + 180) / 360) * 1000;
const BY = (lat: number) => ((83 - lat) / 139) * 420;
const f1 = (n: number) => n.toFixed(1);

/** Bản đồ rotation (Mint-ServicesMap `mp`): toạ độ cảng UN/LOCODE, kinh độ nối liền theo thứ tự ghé, cung cong + mũi tên giữa các cảng liên tiếp. */
export function ServiceMap({ code, rot, circ, ships, pick, onPick }: { code: string; rot: readonly string[]; circ: number; ships: number; pick: string | null; onPick: (p: string | null) => void }) {
  const t = useT();
  const uw = new Map<string, number>();
  let prev: number | undefined;
  for (const c of rot) {
    const ll = PORT_LL[c];
    if (!ll) continue;
    if (!uw.has(c)) {
      let lon = ll[0];
      if (prev !== undefined) { while (lon - prev > 180) lon -= 360; while (prev - lon > 180) lon += 360; }
      uw.set(c, lon);
    }
    prev = uw.get(c);
  }
  const codes = [...uw.keys()];
  const none = codes.length < 1;
  let tf = '';
  let lands: number[] = [];
  const legs: { d: string; ar: string }[] = [];
  const ports: { c: string; x: number; y: number; lx: number; anchor: 'start' | 'end' | 'middle'; ly: number }[] = [];
  if (!none) {
    const xs = codes.map((c) => BX(uw.get(c)!));
    const ys = codes.map((c) => BY(PORT_LL[c]![1]));
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const spX = Math.max(x1 - x0, 30), spY = Math.max(y1 - y0, 18), aw = MAP_W - 128, ah = MAP_H - 116;
    const sc = Math.min(aw / spX, ah / spY, 60);
    const tx = 64 + aw / 2 - ((x0 + x1) / 2) * sc, ty = 60 + ah / 2 - ((y0 + y1) / 2) * sc;
    const P = (c: string): [number, number] => [BX(uw.get(c)!) * sc + tx, BY(PORT_LL[c]![1]) * sc + ty];
    tf = `translate(${f1(tx)} ${f1(ty)}) scale(${sc.toFixed(4)})`;
    const vx0 = -tx / sc, vx1 = (MAP_W - tx) / sc;
    lands = [-1, 0, 1].filter((k) => k * 1000 < vx1 && k * 1000 + 1000 > vx0);
    const seq = rot.filter((p) => uw.has(p));
    for (let j = 1; j < seq.length; j++) {
      const A = P(seq[j - 1]!), B = P(seq[j]!);
      const dx = B[0] - A[0], dy = B[1] - A[1], len = Math.hypot(dx, dy);
      if (len < 2) continue;
      const off = Math.min(Math.max(len * 0.18, 10), 70);
      const C: [number, number] = [(A[0] + B[0]) / 2 + (-dy / len) * off, (A[1] + B[1]) / 2 + (dx / len) * off];
      const u = 0.6;
      const M: [number, number] = [(1 - u) * (1 - u) * A[0] + 2 * u * (1 - u) * C[0] + u * u * B[0], (1 - u) * (1 - u) * A[1] + 2 * u * (1 - u) * C[1] + u * u * B[1]];
      const gx = 2 * (1 - u) * (C[0] - A[0]) + 2 * u * (B[0] - C[0]), gy = 2 * (1 - u) * (C[1] - A[1]) + 2 * u * (B[1] - C[1]);
      const gl = Math.hypot(gx, gy) || 1, ux = gx / gl, uy = gy / gl;
      const ar = `M${f1(M[0] + ux * 6)} ${f1(M[1] + uy * 6)}L${f1(M[0] - ux * 5 - uy * 5)} ${f1(M[1] - uy * 5 + ux * 5)}L${f1(M[0] - ux * 5 + uy * 5)} ${f1(M[1] - uy * 5 - ux * 5)}Z`;
      legs.push({ d: `M${f1(A[0])} ${f1(A[1])}Q${f1(C[0])} ${f1(C[1])} ${f1(B[0])} ${f1(B[1])}`, ar });
    }
    const boxes: number[][] = [[0, 0, 400, 52], [MAP_W - 190, 0, MAP_W, 52], [0, MAP_H - 52, 380, MAP_H], ...codes.map((c) => { const [x, y] = P(c); return [x - 8, y - 8, x + 8, y + 8]; })];
    const hit = (q: number[]) => boxes.some((o) => q[0]! < o[2]! && q[2]! > o[0]! && q[1]! < o[3]! && q[3]! > o[1]!);
    for (const c of codes) {
      const [x, y] = P(c);
      const r = isVnPort(c) ? 7 : 5, lw = c.length * 7.6 + 12;
      const cand: [string, number[]][] = [
        ['start', [x + r + 4, y - 10, x + r + 4 + lw, y + 10]],
        ['end', [x - r - 4 - lw, y - 10, x - r - 4, y + 10]],
        ['up', [x - lw / 2, y - r - 24, x + lw / 2, y - r - 4]],
        ['down', [x - lw / 2, y + r + 4, x + lw / 2, y + r + 24]],
      ];
      const pk = cand.find(([, q]) => !hit(q) && q[0]! > 4 && q[2]! < MAP_W - 4) ?? cand[0]!;
      boxes.push(pk[1]);
      const [kind] = pk;
      ports.push(kind === 'start' ? { c, x, y, lx: x + r + 10, ly: y + 4, anchor: 'start' }
        : kind === 'end' ? { c, x, y, lx: x - r - 10, ly: y + 4, anchor: 'end' }
        : kind === 'up' ? { c, x, y, lx: x, ly: y - r - 10, anchor: 'middle' }
        : { c, x, y, lx: x, ly: y + r + 18, anchor: 'middle' });
    }
  }
  const aria = t('sm.aria', { code, n: codes.length });
  return (
    <section className={m.map} style={{ height: MAP_H }} aria-label={aria}>
      <svg width="100%" height={MAP_H} viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid slice" className={m.svg} aria-hidden="true">
        <g transform={tf}>
          {lands.map((k) => <path key={k} d={LAND_PATH} transform={`translate(${k * 1000} 0)`} className={m.land} vectorEffect="non-scaling-stroke" />)}
        </g>
        {legs.map((g, i) => (
          <g key={i} style={{ color: 'var(--pri)' }} opacity={0.95}>
            <path d={g.d} fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" />
            <path d={g.ar} fill="currentColor" />
          </g>
        ))}
        {ports.map((p) => {
          const vn = isVnPort(p.c), on = pick === p.c;
          return (
            <g key={p.c} role="button" tabIndex={0} aria-pressed={on} aria-label={`${p.c}${vn ? ` · ${t('sm.vnPort')}` : ''} · ${t('sm.viewRot')}`} style={{ cursor: 'pointer' }}
              onClick={() => onPick(on ? null : p.c)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(on ? null : p.c); } }}>
              <circle cx={p.x} cy={p.y} r={(vn ? 7 : 5) + (on ? 2 : 0)} className={`${m.portDot} ${vn ? m.portVn : ''}`} />
              <text x={p.lx} y={p.ly} textAnchor={p.anchor} className={`${m.portTx} ${vn || on ? m.portTxVn : ''}`}>{p.c}</text>
            </g>
          );
        })}
      </svg>
      {none && <div className={m.none}>{t('sm.noCoord')}</div>}
      <div className={m.stats}>
        <span>{t('sm.loop')} <b>{t('sm.days', { n: circ })}</b></span>
        <span>{t('sm.fleet')} <b>{t('sm.ships', { n: ships })}</b></span>
        <span>{t('sm.freq')} <b>{t('sm.weekly')}</b></span>
      </div>
      <div className={m.legend}>
        <span><i className={m.lgSolid} />{t('sm.outbound')}</span>
        <span><i className={m.lgVn} />{t('sm.vnPort')}</span>
      </div>
    </section>
  );
}
